import React from 'react'
import styled from 'styled-components'
import { ChevronRight, Edit, Trash2, User } from 'lucide-react'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent'
import { getTeamIcon } from './teamIcons'

export default function TeamCard({ team, onSelect, onEdit, onDelete }) {
    const TeamIconComponent = getTeamIcon(team.icon);
    const memberCount = (team.members || []).length;
    const hasPanelGrant = (team.grants || []).some(grant => grant.scope_type === 'panel');
    const eventGrantsCount = (team.grants || []).filter(grant => grant.scope_type === 'event').length;
    const hasAccess = hasPanelGrant || eventGrantsCount > 0;

    function scopeSummary() {
        if (hasPanelGrant) return 'Painel inteiro';
        if (eventGrantsCount) return `${eventGrantsCount} evento${eventGrantsCount > 1 ? 's' : ''}`;
        return 'Sem acesso concedido';
    }

    return (
        <CardContainer onClick={() => onSelect(team)} aria-label={team.name}>
            <CardBody>
                <TopRow>
                    <TeamIcon>
                        <TeamIconComponent size={17} color={panelAccent(colors.secondaryPurple)} />
                    </TeamIcon>

                    <CardActions>
                        <IconButton
                            type='button'
                            aria-label={`Editar equipe ${team.name}`}
                            onClick={(e) => { e.stopPropagation(); onEdit(team); }}
                        >
                            <Edit size={14} />
                        </IconButton>
                        <IconButton
                            type='button'
                            aria-label={`Excluir equipe ${team.name}`}
                            onClick={(e) => { e.stopPropagation(); onDelete(team); }}
                        >
                            <Trash2 size={14} color={colors.danger} />
                        </IconButton>
                    </CardActions>
                </TopRow>

                <TeamName>{team.name}</TeamName>

                <InfoRow>
                    <User size={14} color={colors.night} />
                    <InfoText>{memberCount} membro{memberCount !== 1 ? 's' : ''}</InfoText>
                </InfoRow>

                <ScopeBadge $muted={!hasAccess}>
                    {scopeSummary()}
                </ScopeBadge>
            </CardBody>

            <CardFooter>
                <CtaButton type='button' aria-label={`Ver equipe: ${team.name}`}>
                    Ver equipe
                    <ChevronRight size={15} strokeWidth={2.5} />
                </CtaButton>
            </CardFooter>
        </CardContainer>
    )
}

const CardContainer = styled.article`
    width: 100%;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}26;
    overflow: hidden;
    position: relative;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 2px ${colors.nightDark}0F, 0 1px 3px ${colors.nightDark}0A;
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-3px);
        border-color: ${panelAccentAlpha(colors.secondaryPurple, '66')};
        box-shadow: 0 10px 24px ${colors.nightDark}1F;
    }

    &:active {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px ${colors.nightDark}1A;
    }

    &:focus-within {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
        border-color: ${panelAccentAlpha(colors.secondaryPurple, '66')};
    }

    @media (prefers-reduced-motion: reduce) {
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:hover,
        &:active {
            transform: none;
        }
    }
`;

const CardBody = styled.div`
    padding: 16px 16px 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
`;

const TopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const TeamIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
`;

const CardActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const IconButton = styled.button`
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: ${colors.night};
    cursor: pointer;
    transition: background 0.15s ease;
    padding: 0;

    &:hover {
        background: ${colors.neutralBackground};
    }
`;

const TeamName = styled.p`
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
    padding: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
    transition: color 0.2s ease;

    ${CardContainer}:hover &,
    ${CardContainer}:focus-within & {
        color: ${panelAccent(colors.secondaryPurple)};
    }
`;

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const InfoText = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const ScopeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};

    ${({ $muted }) => $muted && `
        background: ${colors.neutralDark};
        color: ${colors.nightLight};
    `}
`;

const CardFooter = styled.div`
    border-top: 1px solid ${colors.night}1A;
    padding: 4px 8px;
`;

const CtaButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    padding: 8px;
    border: none;
    background: transparent;
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    font-family: inherit;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;

    & > svg {
        transition: transform 0.2s ease;
    }

    &:focus-visible {
        outline: none;
    }

    ${CardContainer}:hover & {
        background: #F3EAFF;
    }

    ${CardContainer}:hover & > svg {
        transform: translateX(3px);
    }

    @media (prefers-reduced-motion: reduce) {
        ${CardContainer}:hover & > svg {
            transform: none;
        }
    }
`;
